import { Autocomplete, Box, TextField } from "@mui/material";
import { useCallback, useState } from "react";

import type {
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import type { FC } from "react";

// Define props interface for the Autocomplete component
interface AutocompleteFilterInputProps extends GridFilterInputValueProps {
  options: { label: string; value: string }[];
}

const AutocompleteFilterInput: FC<AutocompleteFilterInputProps> = (props) => {
  const { item, applyValue, options } = props;
  const [value, setValue] = useState<{ label: string; value: string } | null>(
    () => options.find((o) => o.value === item.value) || null,
  );

  const handleFilterChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string | null) => {
      setValue(options.find((o) => o.value === newValue) || null);
      applyValue({ ...item, value: newValue });
    },
    [options, applyValue, item],
  );

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        height: 48,
        pl: "20px",
      }}
    >
      <Autocomplete
        value={value}
        renderInput={(params) => (
          <TextField {...params} variant="standard" placeholder="Select value" size="small" />
        )}
        onChange={(event, newValue) => handleFilterChange(event, newValue?.value ?? null)}
        options={options}
        getOptionLabel={(option) => option.label}
        style={{ width: 150 }}
        size="small"
      />
    </Box>
  );
};

export const autocompleteOperator = <R extends GridValidRowModel>(
  options: { label: string; value: string }[],
  operatorInfo: { label: string; value: string } = { label: "Equals", value: "equals" },
): GridFilterOperator<R, string, string, AutocompleteFilterInputProps> => {
  const operator: GridFilterOperator<R, string, string, AutocompleteFilterInputProps> = {
    ...operatorInfo,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || filterItem.value === undefined || !filterItem.operator) {
        return null;
      }

      return (value: string): boolean => {
        if (value === undefined || value === null || filterItem.value === null) {
          return false;
        }

        return value === String(filterItem.value);
      };
    },
    InputComponent: AutocompleteFilterInput,
    InputComponentProps: { options },
  };

  return operator;
};
